// User and company related types

export interface Company {
  id: string;
  name: string;
  nip: string;
  address: string;
  industry: string;
  plan: 'free' | 'pro' | 'enterprise';
  settings: CompanySettings;
  createdAt: Date;
  updatedAt: Date;
}

export interface CompanySettings {
  monthlyBudget: number;
  budgetAlert80: boolean;
  budgetAlert90: boolean;
  budgetAlert100: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  autoOrderEnabled: boolean;
  currency: string;
}

export interface User {
  id: string;
  companyId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  avatar?: string;
  preferences: UserPreferences;
  permissions: UserPermissions;
  passwordHash?: string; // Only for backend/storage
  emailVerified: boolean;
  status: import('./auth').AuthStatus;
  twoFactorEnabled?: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
}

export enum UserRole {
  ADMIN = 'admin',
  ORDERING_MANAGER = 'ordering_manager',
  MANAGER = 'manager',
  USER = 'user'
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  defaultView: string;
  notificationSettings: NotificationSettings;
}

export interface UserPermissions {
  canOrder: boolean;
  canEditStock: boolean;
  canViewReports: boolean;
  canManageUsers: boolean;
  canChangeSettings: boolean;
  canApproveOrders: boolean;
}

export interface NotificationSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
  lowStockAlerts: boolean;
  budgetAlerts: boolean;
}

// Default configurations
export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  monthlyBudget: 5000,
  budgetAlert80: true,
  budgetAlert90: true,
  budgetAlert100: true,
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
  autoOrderEnabled: false,
  currency: 'PLN'
};

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  theme: 'light',
  language: 'pl',
  defaultView: 'dashboard',
  notificationSettings: {
    email: true,
    push: true,
    sms: false,
    lowStockAlerts: true,
    budgetAlerts: true
  }
};

export const ROLE_PERMISSIONS: Record<UserRole, UserPermissions> = {
  [UserRole.ADMIN]: {
    canOrder: true,
    canEditStock: true,
    canViewReports: true,
    canManageUsers: true,
    canChangeSettings: true,
    canApproveOrders: true
  },
  [UserRole.ORDERING_MANAGER]: {
    canOrder: true,
    canEditStock: true,
    canViewReports: true,
    canManageUsers: false,
    canChangeSettings: false,
    canApproveOrders: true
  },
  [UserRole.MANAGER]: {
    canOrder: false,
    canEditStock: false,
    canViewReports: true,
    canManageUsers: false,
    canChangeSettings: false,
    canApproveOrders: true
  },
  [UserRole.USER]: {
    canOrder: false,
    canEditStock: false,
    canViewReports: false,
    canManageUsers: false,
    canChangeSettings: false,
    canApproveOrders: false
  }
};